import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { IAddress } from '../../../../../../../../ui-components/address-autocomplete/models/address';
import { CountryService } from '../../../../../../../../shared/services/location/country.service';
import { CityService } from '../../../../../../../../shared/services/location/city.service';
import { DistrictService } from '../../../../../../../../shared/services/location/district.service';
import { catchError, forkJoin, of } from 'rxjs';
import { AppMessageService } from '../../../../../../../../shared/services/app-message/app-message.service';
import { TranslateService } from '@ngx-translate/core';
import { ParameterService } from '../../../../../../../../shared/services/parameter/parameter.service';
import { CasePatientAddressPopupComponent } from '../case-patient-address-popup/case-patient-address-popup.component';
import {
  ICasePatient,
  ICasePatientAddress,
} from '../../../../../../interfaces/interfaces';

@Component({
  selector: 'case-address-autocomplete',
  templateUrl: './case-address.component.html',
  styleUrls: ['./case-address.component.scss'],
})
export class CaseAddressComponent implements OnInit, OnChanges {
  @ViewChild('addressPopup') addressPopup: CasePatientAddressPopupComponent;
  @Input() disableAll: boolean = false;
  @Input() isReadOnly: boolean = false;
  @Input() patient: ICasePatient;
  @Output() addressChangeOn: EventEmitter<IAddress> = new EventEmitter();

  addressForm: FormGroup;
  isManualAddress: boolean = false;
  manuelFormattedAddress: string = '';
  manuelCountry: any = null;
  manuelCity: any = null;
  manuelDistrict: any = null;
  manuelPostalCode: any = '';
  manuelStreet: any = '';
  manuelLatitude: any = 0;
  manuelLongitude: any = 0;
  manuelDescription: string = '';
  countries: any[] = [];
  cities: any[] = [];
  filteredCities: any[] = [];
  districts: any[] = [];
  filteredDistricts: any[] = [];
  turkeyId: string;
  istanbulId: string;

  // address: IAddress = {
  //   city: '',
  //   district: '',
  //   street: '',
  //   postalCode: '',
  //   formattedAddress: '',
  //   latitude: 0.0,
  //   longitude: 0.0,
  // };

  constructor(
    private readonly countryService: CountryService,
    private readonly cityService: CityService,
    private readonly districtService: DistrictService,
    private readonly messageService: AppMessageService,
    private readonly translate: TranslateService,
    private readonly parameterService: ParameterService
  ) {
    this.addressForm = new FormGroup(
      {
        country: new FormControl(
          { value: '', disabled: true },
          Validators.required
        ),
        city: new FormControl(
          { value: '', disabled: true },
          Validators.required
        ),
        district: new FormControl(
          { value: '', disabled: true },
          Validators.required
        ),
        street: new FormControl({ value: '', disabled: true }),
        postalCode: new FormControl({ value: '', disabled: true }),
        formattedAddress: new FormControl(
          { value: '', disabled: this.disableAll },
          Validators.required
        ),
        latitude: new FormControl({ value: 0, disabled: true }, [
          Validators.min(-90),
          Validators.max(90),
        ]),
        longitude: new FormControl({ value: 0, disabled: true }, [
          Validators.min(-180),
          Validators.max(180),
        ]),
        addressDescription: new FormControl('', Validators.maxLength(250)),
      },
      { validators: this.addressValidator }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableAll']) {
      if (this.disableAll) {
        this.addressForm.controls['formattedAddress'].disable();
      } else {
        this.addressForm.controls['formattedAddress'].enable();
      }
    }
  }

  ngOnInit() {
    forkJoin({
      countries: this.countryService.getAll().pipe(catchError(() => of([]))),
      cities: this.cityService.getAll().pipe(catchError(() => of([]))),
      districts: this.districtService.getAll().pipe(catchError(() => of([]))),
      turkeyId: this.parameterService
        .getParameterValueByKeyname('turkiye-country-id')
        .pipe(catchError(() => of(null))),
      istanbulId: this.parameterService
        .getParameterValueByKeyname('istanbul-city-id')
        .pipe(catchError(() => of(null))),
    }).subscribe(({ countries, cities, districts, turkeyId, istanbulId }) => {
      this.countries = countries?.map((c) => ({
        label: c?.name,
        value: c?.id,
      }));

      this.cities = cities?.map((c) => ({
        label: c?.name,
        value: c?.id,
        country: c?.country?.id,
      }));

      this.districts = districts?.map((t) => ({
        label: t?.name,
        value: t?.id,
        city: t?.city?.id,
      }));

      this.turkeyId = turkeyId?.value ?? '';
      this.istanbulId = istanbulId?.value ?? '';
    });
  }

  public onPlaceChanged(place: any) {
    this.addressForm.reset();

    this.fillAddressFormFromPlace(place);

    this.addressForm.get('formattedAddress')?.setValue(place.formatted_address);

    if (place?.geometry?.location) {
      this.addressForm.get('latitude')?.setValue(place.geometry.location.lat());
      this.addressForm.get('longitude')?.setValue(place.geometry.location.lng());
    }

    this.modelChange();
  }

  private fillAddressFormFromPlace(place: any) {
    if (!place?.address_components) return;

    for (const element of place.address_components) {
      if (element.types?.includes('administrative_area_level_2')) {
        this.addressForm.get('district')?.setValue(element.long_name);
      }
      if (element.types?.includes('administrative_area_level_1')) {
        this.addressForm.get('city')?.setValue(element.long_name);
      }
      if (element.types?.includes('country')) {
        this.addressForm.get('country')?.setValue(element.long_name);
      }
      if (element.types?.includes('route')) {
        this.addressForm.get('street')?.setValue(element.long_name);
      }
      if (element.types?.includes('postal_code')) {
        this.addressForm.get('postalCode')?.setValue(element.long_name);
      }
    }
  }

  onManualAddressChange() {
    this.resetForm();
  }

  onDesciptionChange() {
    this.modelChange();
  }

  onAddressChanged() {
    this.addressForm.patchValue({
      formattedAddress: this.manuelFormattedAddress,
      country: this.manuelCountry?.label,
      city: this.manuelCity?.label,
      district: this.manuelDistrict?.label,
      postalCode: this.manuelPostalCode,
      street: this.manuelStreet,
      addressDescription: this.manuelDescription,
      latitude: this.manuelLatitude,
      longitude: this.manuelLongitude,
    });

    this.addressForm.markAllAsTouched();
    this.addressForm.updateValueAndValidity();
    this.modelChange();
  }

  setAddress(address?: any, markAsPristine?: boolean) {
    if (address) {
      const currentDescription = address?.addressDescription ?? this.addressForm.get('addressDescription')?.value ?? '';
      this.manuelDescription = currentDescription;
      this.addressForm.patchValue({ ...address , addressDescription: currentDescription});
      this.isManualAddress = address?.isManualAddress ?? false;
      if (this.isManualAddress) {
        if (!this.countries || this.countries.length === 0) {
          // countries daha yüklenmemişse geciktir
          setTimeout(() => this.setAddress(address, markAsPristine), 100);
          return;
        }
        this.manuelFormattedAddress = address?.formattedAddress;
        this.manuelCountry = this.countries.find(
          (c) =>
            c?.label?.toLocaleLowerCase('tr') ===
            address?.country?.toLocaleLowerCase('tr')
        );

        this.filteredCities = this.cities?.filter(
          (c) => c?.country === this.manuelCountry?.value
        );
        this.manuelCity = this.cities.find(
          (c) =>
            c?.label?.toLocaleLowerCase('tr') ===
            address?.city?.toLocaleLowerCase('tr')
        );

        this.filteredDistricts = this.districts?.filter(
          (c) => c?.city === this.manuelCity?.value
        );
        this.manuelDistrict = this.districts.find(
          (c) =>
            c?.label?.toLocaleLowerCase('tr') ===
            address?.district?.toLocaleLowerCase('tr')
        );
      }
      this.addressChangeOn.emit({
        ...this.addressForm.getRawValue(),
        isManualAddress: this.isManualAddress,
        markAsPristine,
      });
    } else {
      this.addressForm.patchValue({
        city: '',
        district: '',
        country: '',
        street: '',
        postalCode: '',
        formattedAddress: '',
        addressDescription: '',
        latitude: 0,
        longitude: 0,
      });
      this.resetManuelForm();
      this.addressChangeOn.emit({
        ...this.addressForm.getRawValue(),
        isManualAddress: this.isManualAddress,
        markAsPristine,
      });
    }
  }

  resetForm() {
  const addressDescription = this.isManualAddress
    ? this.addressForm.get('addressDescription')?.value
    : ''; 
    this.addressForm.patchValue({
      city: '',
      district: '',
      country: '',
      street: '',
      postalCode: '',
      formattedAddress: '',
      addressDescription: addressDescription,
      latitude: 0,
      longitude: 0,
    });
    this.resetManuelForm(addressDescription);
    this.modelChange();
  }

  resetManuelForm(addressDescription?:string) {
    //manuel de default ülke ve şehir türkiye istanbul olur
    this.manuelCountry = this.turkeyId && !Number.isNaN(Number(this.turkeyId))
      ? this.countries.find((c) => c.value === Number(this.turkeyId))
      : null;
    this.filteredCities = this.cities?.filter(
      (c) => c?.country === this.manuelCountry?.value
    );
    this.manuelCity = this.istanbulId && !Number.isNaN(Number(this.istanbulId))
      ? this.cities.find((c) => c.value === Number(this.istanbulId))
      : null;
    this.filteredDistricts = this.districts?.filter(
      (c) => c?.city === this.manuelCity?.value
    );
    this.manuelDistrict = null;
    this.manuelFormattedAddress = '';
    this.manuelLatitude = 0;
    this.manuelLongitude = 0;
    this.manuelPostalCode = '';
    this.manuelStreet = '';
    this.manuelDescription = addressDescription ?? '';
  }

  onCountryChanged(event: any) {
    this.manuelCity = null;
    this.manuelDistrict = null;
    this.filteredCities = this.cities?.filter(
      (c) => c?.country === event?.value
    );
    this.onAddressChanged();
  }
  onCityChanged(event: any) {
    this.manuelDistrict = null;
    this.filteredDistricts = this.districts?.filter(
      (c) => c?.city === event?.value
    );
    this.onAddressChanged();
  }

  public validateAddressForm(): boolean {
    this.addressForm.markAllAsTouched();
    this.addressForm.updateValueAndValidity();
    return this.addressForm.valid;
  }

  public addressValidator(form?: AbstractControl): ValidationErrors | null {
    // Disabled kontrollerin değerlerini de almak için getRawValue kullanıyoruz.
    const rawValue = form ? form.getRawValue() : this.addressForm.getRawValue();

    // Formdaki alanlardan herhangi biri dolu ise validasyonu çalıştırıyoruz.
    const fields = [
      'country',
      'city',
      'district',
      'street',
      'postalCode',
      'formattedAddress',
      'latitude',
      'longitude',
      'addressDescription',
    ];
    const anyFilled = fields.some((field) => {
      const value = rawValue[field];
      if (typeof value === 'string') {
        return value.trim() !== '';
      } else if (typeof value === 'number') {
        return value !== 0;
      }
      return !!value;
    });

    if (!anyFilled) {
      return null;
    }

    const errors: any = {};
    // Zorunlu alanlar: ülke, şehir, ilçe, açık adres (formattedAddress)
    if (
      !rawValue.country ||
      (typeof rawValue.country === 'string' && rawValue.country.trim() === '')
    ) {
      errors.country = 'Ülke bilgisi gereklidir.';
    }
    if (
      !rawValue.city ||
      (typeof rawValue.city === 'string' && rawValue.city.trim() === '')
    ) {
      errors.city = 'Şehir bilgisi gereklidir.';
    }
    if (
      !rawValue.district ||
      (typeof rawValue.district === 'string' && rawValue.district.trim() === '')
    ) {
      errors.district = 'İlçe bilgisi gereklidir.';
    }
    if (
      !rawValue.formattedAddress ||
      (typeof rawValue.formattedAddress === 'string' &&
        rawValue.formattedAddress.trim() === '')
    ) {
      errors.formattedAddress = 'Adres bilgisi gereklidir.';
    }

    return Object.keys(errors).length ? errors : null;
  }

  modelChange() {
    this.addressChangeOn.emit({
      ...this.addressForm.getRawValue(),
      isManualAddress: this.isManualAddress,
    });
  }

  onNavigateIconClick(): void {
    const adresFormValue =
      this.addressForm?.controls['formattedAddress']?.value;
    const latitude = this.addressForm?.controls['latitude']?.value;
    const longitude = this.addressForm?.controls['longitude']?.value;
    /*
    if (adresFormValue) {
      // Navigate to Google Maps with formatted address
      const formattedAddress = encodeURIComponent(latitude+', '+longitude);
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${formattedAddress}`,
        '_blank'
      );
    } else */
      if (latitude && longitude) {
      // Navigate to Google Maps with latitude and longitude

      window.open(
        `https://www.google.com/maps?q=${latitude},${longitude}`,
        '_blank'
      );
    } else {
      this.messageService.warningToast(
        this.translate.instant('common.warning'),
        this.translate.instant('common.no-valid-address')
      );
      return;
    }
  }

  onSearchIconClick() {
    this.addressPopup.initilize(this.patient.addresses);
  }

  saveAddress(selectedAddress: ICasePatientAddress) {
    this.isManualAddress = false;
    this.setAddress({
      ...selectedAddress,
      isManualAddress: this.isManualAddress,
    });
  }
}